import {defineStore} from 'pinia'
import {computed, ref} from 'vue'
import type {useContext, useDialogs, useProcesses} from '@hantera/portal-app'
import type useBackend from '@/composition/useBackend'
import type {Client, RoleListItem, Session} from '../../backend/iam.types'
import {useIamEventsStore} from './iam-events-store'

export const useClientStore = (
  clientId: string,
  services: {
    context: ReturnType<typeof useContext>
    dialogs: ReturnType<typeof useDialogs>
    processes: ReturnType<typeof useProcesses>
    iamService: ReturnType<typeof useBackend>['iam']
  }
) => {
  return defineStore(`client-${clientId}`, () => {
    // State
    const client = ref<Client | null>(null)
    const secrets = ref<any[]>([])
    const sessions = ref<Session[]>([])
    const roles = ref<RoleListItem[]>([])
    const isLoading = ref(false)

    // Pending changes (null = no changes)
    const pendingAcl = ref<string[] | null>(null)
    const pendingRoleIds = ref<string[] | null>(null)
    const pendingScopes = ref<string[] | null>(null)
    const pendingGrantTypes = ref<string[] | null>(null)
    const pendingRedirectUris = ref<string[] | null>(null)
    const pendingClientCredentials = ref<boolean | null>(null)

    // Services from parameters
    const { context, dialogs, processes, iamService } = services

    // Permissions
    const canWrite = context.authorize('iam/clients:write')

    // Abort controller for cancellation
    let refreshController: AbortController | undefined

    // Getters
    const displayName = () => {
      if (!client.value) return ''
      return client.value.name || client.value.id
    }

    const roleCount = () => client.value?.roles.length ?? 0
    const sessionCount = () => sessions.value.length
    const secretCount = () => secrets.value.filter(s => !s.isRevoked && !s.isExpired).length

    const hasAclChanges = computed(() => pendingAcl.value !== null)
    const hasRolesChanges = computed(() => pendingRoleIds.value !== null)
    const hasScopesChanges = computed(() => pendingScopes.value !== null)
    const hasGrantTypesChanges = computed(() => pendingGrantTypes.value !== null)
    const hasRedirectUrisChanges = computed(() => pendingRedirectUris.value !== null)
    const hasClientCredentialsChanges = computed(() => pendingClientCredentials.value !== null)
    const hasUserAuthChanges = computed(() => 
      hasGrantTypesChanges.value || 
      hasRedirectUrisChanges.value || 
      hasScopesChanges.value
    )
    const hasServiceAccountChanges = computed(() =>
      hasClientCredentialsChanges.value ||
      hasRolesChanges.value ||
      hasAclChanges.value
    )
    const hasUnsavedChanges = computed(() => hasServiceAccountChanges.value || hasUserAuthChanges.value)

    // Compute which authentication modes are enabled
    const hasClientCredentials = computed(() => 
      client.value?.grantTypes?.includes('client_credentials') ?? false
    )
    
    const hasUserAuth = computed(() => 
      ['authorization_code', 'implicit'].some(gt => 
        client.value?.grantTypes?.includes(gt)
      ) ?? false
    )

    // Actions
    async function refresh() {
      if (!clientId) return

      if (refreshController) {
        refreshController.abort('stale')
      }

      refreshController = new AbortController()
      isLoading.value = true

      try {
        const [clientData, secretsData, sessionsData, rolesResponse] = await Promise.all([
          iamService.getClient(clientId),
          iamService.getClientSecrets(clientId),
          iamService.getClientSessions(clientId),
          iamService.getRoles({}),
        ])

        client.value = clientData
        secrets.value = secretsData
        sessions.value = sessionsData
        roles.value = rolesResponse.roles
      } catch (error: any) {
        if (error !== 'stale') {
          console.error('Failed to load client details:', error)
          processes.error({ message: 'Failed to load client details' })
        }
      } finally {
        refreshController = undefined
        isLoading.value = false
      }
    }

    async function updateProfile(updates: {
      name?: string
      description?: string
      redirectUris?: string[]
      grantTypes?: string[]
      scopes?: string[]
    }) {
      if (!client.value) return

      try {
        await iamService.updateClient(client.value.id, updates, client.value.eTag)
        await refresh()

        processes.createProcess({
          title: 'Client Updated',
          description: 'Client details updated successfully',
        }).complete()
      } catch (error: any) {
        console.error('Failed to update client:', error)
        processes.error({
          message: error.response?.data?.message || 'Failed to update client',
        })
        throw error
      }
    }

    async function deleteClient() {
      if (!client.value) return

      const confirmed = await dialogs.confirm(
        `Are you sure you want to delete ${displayName()}? This action cannot be undone. All secrets will be revoked.`,
        'Delete Client',
        undefined,
        undefined,
        true
      )

      if (!confirmed) return

      try {
        await iamService.deleteClient(clientId)
        
        // Broadcast to all ClientsView instances
        const iamEvents = useIamEventsStore()
        iamEvents.emitClientsListChanged()
        
        processes.createProcess({
          description: 'Client deleted',
        }).complete()
      } catch (error) {
        console.error('Failed to delete client:', error)
        processes.error({ message: 'Failed to delete client' })
      }
    }

    async function generateSecret(
      description?: string,
      expiresAt?: Date
    ): Promise<{ secretId: string; secret: string } | null> {
      if (!client.value) return null

      try {
        const result = await iamService.generateClientSecret(
          client.value.id,
          { description, expiresAt }
        )
        await refresh()
        
        processes.createProcess({
          description: 'Secret generated',
        }).complete()

        return result
      } catch (error) {
        console.error('Failed to generate secret:', error)
        processes.error({ message: 'Failed to generate secret' })
        return null
      }
    }

    async function revokeSecret(secretId: string) {
      if (!client.value) return

      const confirmed = await dialogs.confirm(
        'Revoke this secret? Applications using it will no longer be able to authenticate.',
        'Revoke Secret'
      )

      if (!confirmed) return

      try {
        await iamService.revokeClientSecret(client.value.id, secretId)
        await refresh()
        
        processes.createProcess({
          description: 'Secret revoked',
        }).complete()
      } catch (error) {
        console.error('Failed to revoke secret:', error)
        processes.error({ message: 'Failed to revoke secret' })
      }
    }

    async function saveAcl() {
      if (!client.value || pendingAcl.value === null) return

      try {
        await iamService.updateClientAcl(
          client.value.id,
          pendingAcl.value,
          client.value.eTag
        )

        await refresh()
        pendingAcl.value = null

        processes.createProcess({
          description: 'Permissions updated',
        }).complete()
      } catch (error) {
        console.error('Failed to update permissions:', error)
        processes.error({ message: 'Failed to update permissions' })
      }
    }

    async function saveRoles() {
      if (!client.value || pendingRoleIds.value === null) return

      try {
        await iamService.updateClientRoles(
          client.value.id,
          pendingRoleIds.value,
          client.value.eTag
        )

        await refresh()
        pendingRoleIds.value = null

        processes.createProcess({
          description: 'Roles updated',
        }).complete()
      } catch (error) {
        console.error('Failed to update roles:', error)
        processes.error({ message: 'Failed to update roles' })
      }
    }

    async function saveScopes() {
      if (!client.value || pendingScopes.value === null) return

      try {
        await iamService.updateClient(
          client.value.id,
          { scopes: pendingScopes.value },
          client.value.eTag
        )

        await refresh()
        pendingScopes.value = null

        processes.createProcess({
          description: 'Scopes updated',
        }).complete()
      } catch (error) {
        console.error('Failed to update scopes:', error)
        processes.error({ message: 'Failed to update scopes' })
      }
    }

    async function saveUserAuth() {
      if (!client.value) return
      if (!hasUserAuthChanges.value) return

      const updates: any = {}
      
      if (pendingGrantTypes.value !== null) {
        const grantTypes = [...pendingGrantTypes.value]
        if (client.value.grantTypes?.includes('client_credentials')) {
          grantTypes.push('client_credentials')
        }
        updates.grantTypes = grantTypes
      }
      
      if (pendingRedirectUris.value !== null) {
        updates.redirectUris = pendingRedirectUris.value
      }
      
      if (pendingScopes.value !== null) {
        updates.scopes = pendingScopes.value
      }

      try {
        await iamService.updateClient(
          client.value.id,
          updates,
          client.value.eTag
        )

        await refresh()
        pendingGrantTypes.value = null
        pendingRedirectUris.value = null
        pendingScopes.value = null

        processes.createProcess({
          description: 'User authentication updated',
        }).complete()
      } catch (error) {
        console.error('Failed to update user authentication:', error)
        processes.error({ message: 'Failed to update user authentication' })
      }
    }

    async function saveServiceAccount() {
      if (!client.value) return
      if (!hasServiceAccountChanges.value) return

      try {
        // Update grant types if needed
        if (pendingClientCredentials.value !== null) {
          let grantTypes = client.value.grantTypes?.filter(gt => gt !== 'client_credentials') ?? []
          if (pendingClientCredentials.value) {
            grantTypes.push('client_credentials')
          }
          await iamService.updateClient(
            client.value.id,
            { grantTypes },
            client.value.eTag
          )
          await refresh()
        }

        // Update roles if needed (now has fresh eTag from refresh)
        if (pendingRoleIds.value !== null) {
          await iamService.updateClientRoles(
            client.value.id,
            pendingRoleIds.value,
            client.value.eTag
          )
          await refresh()
        }

        // Update ACL if needed (now has fresh eTag from refresh)
        if (pendingAcl.value !== null) {
          await iamService.updateClientAcl(
            client.value.id,
            pendingAcl.value,
            client.value.eTag
          )
        }

        await refresh()
        pendingClientCredentials.value = null
        pendingRoleIds.value = null
        pendingAcl.value = null

        processes.createProcess({
          description: 'Service account updated',
        }).complete()
      } catch (error) {
        console.error('Failed to update service account:', error)
        processes.error({ message: 'Failed to update service account' })
      }
    }

    function discardAcl() {
      pendingAcl.value = null
    }

    function discardRoles() {
      pendingRoleIds.value = null
    }

    function discardScopes() {
      pendingScopes.value = null
    }

    function discardUserAuth() {
      pendingGrantTypes.value = null
      pendingRedirectUris.value = null
      pendingScopes.value = null
    }

    function discardServiceAccount() {
      pendingClientCredentials.value = null
      pendingRoleIds.value = null
      pendingAcl.value = null
    }

    return {
      // State
      client,
      secrets,
      sessions,
      roles,
      isLoading,
      pendingAcl,
      pendingRoleIds,
      pendingScopes,
      pendingGrantTypes,
      pendingRedirectUris,
      pendingClientCredentials,

      // Permissions
      canWrite,

      // Getters
      displayName,
      roleCount,
      sessionCount,
      secretCount,
      hasAclChanges,
      hasRolesChanges,
      hasScopesChanges,
      hasGrantTypesChanges,
      hasRedirectUrisChanges,
      hasClientCredentialsChanges,
      hasUserAuthChanges,
      hasServiceAccountChanges,
      hasUnsavedChanges,
      hasClientCredentials,
      hasUserAuth,

      // Services
      dialogs,
      processes,
      iamService,

      // Actions
      refresh,
      updateProfile,
      deleteClient,
      generateSecret,
      revokeSecret,
      saveAcl,
      saveRoles,
      saveScopes,
      saveUserAuth,
      saveServiceAccount,
      discardAcl,
      discardRoles,
      discardScopes,
      discardUserAuth,
      discardServiceAccount,
    }
  })()
}
